import { useCreate, useDelete, useList, useUpdate } from "@refinedev/core";
import type { InsightComment, InsightCommentBody } from "../types";
import { getInsightsBaseUrl } from "../helpers/insights";

export const useInsightComments = (
  organizationId: string,
  insightId: string | undefined,
  projectId?: string
): {
  comments: InsightComment[];
  isLoading: boolean;
  isPosting: boolean;
  isUpdating: boolean;
  isRemoving: boolean;
  addComment: (body: string, onSuccess?: () => void) => void;
  editComment: (
    commentId: string,
    body: string,
    onSuccess?: () => void
  ) => void;
  removeComment: (commentId: string, onSuccess?: () => void) => void;
} => {
  const baseResource = `${getInsightsBaseUrl(organizationId, projectId)}/${insightId}/comments`;

  const { data, isLoading, refetch } = useList<InsightComment>({
    resource: baseResource,
    queryOptions: {
      enabled: Boolean(organizationId && insightId),
      staleTime: 0,
    },
    errorNotification: false,
  });

  const { mutate: createComment, isPending: isPosting } = useCreate();

  const { mutate: updateComment, isPending: isUpdating } =
    useUpdate<InsightComment>();

  const { mutate: deleteComment, isPending: isRemoving } = useDelete();

  const refetchComments = () => {
    refetch().catch((error) =>
      console.error("Failed to refetch comments", error)
    );
  };

  const addComment = (body: string, onSuccess?: () => void) => {
    if (!insightId) return;
    createComment(
      {
        resource: baseResource,
        values: { body } satisfies InsightCommentBody,
        successNotification: false,
        errorNotification: false,
      },
      {
        onSuccess: () => {
          refetchComments();
          onSuccess?.();
        },
      }
    );
  };

  const editComment = (
    commentId: string,
    body: string,
    onSuccess?: () => void
  ) => {
    if (!insightId) return;
    updateComment(
      {
        resource: baseResource,
        id: commentId,
        values: { body } satisfies InsightCommentBody,
        successNotification: false,
        errorNotification: false,
      },
      {
        onSuccess: () => {
          refetchComments();
          onSuccess?.();
        },
      }
    );
  };

  const removeComment = (commentId: string, onSuccess?: () => void) => {
    if (!insightId) return;
    deleteComment(
      {
        resource: baseResource,
        id: commentId,
        successNotification: false,
        errorNotification: false,
      },
      {
        onSuccess: () => {
          refetchComments();
          onSuccess?.();
        },
      }
    );
  };

  return {
    comments: data?.data ?? [],
    isLoading,
    isPosting,
    isUpdating,
    isRemoving,
    addComment,
    editComment,
    removeComment,
  };
};
